import React, { useEffect, useRef, useState } from "react";
import "./ConceptXIntro.css";

function ConceptXLogo() {
  return (
    <svg
      className="concept-logo"
      viewBox="0 0 260 240"
      aria-hidden="true"
    >
      <defs>
        <linearGradient
          id="logoBlue"
          x1="110"
          y1="0"
          x2="110"
          y2="140"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#00D9F5" />
          <stop offset="45%" stopColor="#149BFF" />
          <stop offset="100%" stopColor="#4945FF" />
        </linearGradient>

        <linearGradient
          id="logoPurple"
          x1="148"
          y1="100"
          x2="148"
          y2="240"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#4666FF" />
          <stop offset="50%" stopColor="#7038FF" />
          <stop offset="100%" stopColor="#AE00F5" />
        </linearGradient>
      </defs>

      {/* Upper blue wing */}
      <path
        className="logo-blue"
        fill="url(#logoBlue)"
        d="M 0 0 L 110 62 L 220 0 L 110 110 L 110 140 Z"
      />

      {/* Lower purple wing */}
      <path
        className="logo-purple"
        fill="url(#logoPurple)"
        d="M 148 100 L 260 240 L 148 178 L 40 240 L 148 132 Z"
      />
    </svg>
  );
}

function EnergyStroke() {
  return (
    <svg
      className="energy-svg"
      viewBox="0 0 1920 1080"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <filter id="energyGlow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="10" result="blur1" />
          <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur2" />

          <feMerge>
            <feMergeNode in="blur1" />
            <feMergeNode in="blur2" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        <linearGradient
          id="energyGradient"
          x1="0"
          y1="1"
          x2="1"
          y2="0"
        >
          <stop offset="0%" stopColor="#00E8FF" />
          <stop offset="45%" stopColor="#FFFFFF" />
          <stop offset="100%" stopColor="#B53CFF" />
        </linearGradient>
      </defs>

      {/* Trace from the top toward the lower left, as in Final.mp4. */}
      <path
        className="energy-path energy-path-one"
        pathLength="600"
        d="M 1025 185 L 1025 435 L 675 785"
      />

      {/* Trace the chevron from right to left. */}
      <path
        className="energy-path energy-path-two"
        pathLength="1200"
        d="M 1405 535 L 970 285 L 340 720"
      />
    </svg>
  );
}

export default function ConceptXIntro({
  onComplete,
  duration = 4572,
}) {
  const [finished, setFinished] = useState(false);
  const onCompleteRef = useRef(onComplete);
  // Timings in seconds, measured against public/Final.mp4 (4.572 seconds).
  // Scale CSS and the completion timer together when duration is overridden.
  const phaseTime = (seconds) => `${seconds * duration / 4.572}ms`;
  const timing = {
    "--intro-first-duration": phaseTime(1.05),
    "--intro-sweep-delay": phaseTime(1.1),
    "--intro-sweep-duration": phaseTime(0.75),
    "--intro-brand-delay": phaseTime(1.95),
    "--intro-brand-duration": phaseTime(0.45),
    "--intro-glow-delay": phaseTime(1.95),
    "--intro-cyan-duration": phaseTime(1.2),
    "--intro-purple-duration": phaseTime(1.4),
    "--intro-text-delay": phaseTime(3.85),
    "--intro-text-duration": phaseTime(0.65),
  };

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    setFinished(false);
    const timer = window.setTimeout(() => {
      setFinished(true);
      if (onCompleteRef.current) onCompleteRef.current();
    }, duration);

    return () => window.clearTimeout(timer);
  }, [duration]);

  return (
    <div
      className={`concept-intro ${finished ? "is-finished" : ""}`}
      style={timing}
      aria-hidden="true"
    >
      {/* Background */}
      <div className="intro-background" />

      {/* Corner atmospheric lighting */}
      <div className="corner-glow corner-glow-cyan" />
      <div className="corner-glow corner-glow-purple" />

      {/* Energy animation */}
      <EnergyStroke />

      {/* Logo + title */}
      <div className="brand-lockup">
        <ConceptXLogo />

        <div className="brand-name">
          Concept X
        </div>
      </div>
    </div>
  );
}
